import apiClient from '@app/config/axios.config'
import { Endpoint } from '@app/config/endpoints'
import { useQuery } from '@tanstack/react-query'
import {
  Article,
  GuideMainTab,
  GuideMainTopics,
  MainTopic,
  StrapiResponse,
  SubTab,
} from '../types/guide.types'
import { atom } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'

// Derived atom that computes home page content from the query data
export const mainTabsAtom = atomWithQuery(() => ({
  queryKey: ['mainTopics'],
  queryFn: fetchAllTabs,
}))

export const homePageContentAtom = atom<MainTopic | null>((get) => {
  const queryResult = get(mainTabsAtom)
  const data = queryResult.data

  if (!data) return null

  const homeContent = data.find(
    (content: GuideMainTab) => content.slug === GuideMainTopics.GET_STARTED,
  )

  if (!homeContent) return null

  return {
    ...homeContent,
    description: homeContent.description ?? '',
  } as MainTopic
})

export const useMainTabs = () => {
  const { data, isLoading, error } = useQuery<GuideMainTab[]>({
    queryKey: ['mainTopics'],
    queryFn: () => fetchAllTabs(),
  })

  return { data, isLoading, error }
}

export const useSubTabBySlug = (subTabSlug: string) => {
  return useQuery<SubTab>({
    queryKey: ['sub-topic-content', subTabSlug],
    queryFn: () => fetchSubBySlug(subTabSlug),
    enabled: !!subTabSlug,
  })
}

export const fetchAllTabs = async (): Promise<GuideMainTab[]> => {
  const response = await apiClient.get(Endpoint.getAllGuideTabs)

  return response.data.data
}

export const fetchSubBySlug = async (subTopicSlug: string): Promise<SubTab> => {
  const response = await apiClient.get(
    `/cms/sub-tabs?filters[slug][$eq]=${subTopicSlug}&fields[0]=title&fields[1]=slug&populate[main_topic][fields][0]=title&populate[main_topic][fields][1]=slug&populate[main_topic][fields][2]=description&populate[articles][fields][0]=title&populate[articles][fields][1]=slug&populate[articles][fields][2]=description&populate[articles][populate][hero_image][fields][0]=url&populate[articles][populate][hero_image][fields][1]=alternativeText`,
  )

  return response.data.data[0]
}

export interface ArticleResponse extends StrapiResponse {
  data: Article[]
}
export const fetchArticleBySlug = async (slug: string): Promise<ArticleResponse> => {
  const response = await apiClient.get(`/cms/articles?filters[slug][$eq]=${slug}&populate=*`)

  return response.data
}
